/**
 * ============================================================================
 * EverythingAnswered — Controller (client)
 * ============================================================================
 * Purpose: Own UI state, analytics, and modal actions for the FAQ section.
 * No JSX — view consumes the returned ViewModel.
 * ============================================================================
 */

"use client";

import { useCallback, useEffect, useMemo, useState, useTransition } from "react";
import { IModalActions, ModalTypes } from "@/types/modal";
import { useModalDispatch } from "@/context/ModalContext";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue, updateStateSession } from "@/context/session";
import { mapQuestionsMapping } from "../mapper/everything-answered.mapper";
import type {
  EverythingAnsweredProps,
  EverythingAnsweredViewModel,
  FaqQuestion,
} from "../model/everything-answered.types";

const SECTION_NAME = "Everything You’re Wondering About";

export function useEverythingAnsweredController({
  associatedPrograms,
  data,
  heading,
}: EverythingAnsweredProps): EverythingAnsweredViewModel {
  const personas = useMemo(() => mapQuestionsMapping(data), [data]);
  const [activeTab, setActiveTab] = useState(0);
  const [activeQuestion, setActiveQuestion] = useState<FaqQuestion | null>(null);
  const [, startTransition] = useTransition();
  const modalDispatch = useModalDispatch();

  const currentPersona = personas[activeTab];
  const { leftQuestions, rightQuestions } = useMemo(
    () => ({
      leftQuestions:
        currentPersona?.questions?.filter((q) => q.side === "left") ?? [],
      rightQuestions:
        currentPersona?.questions?.filter((q) => q.side === "right") ?? [],
    }),
    [currentPersona]
  );

  const handleTabChange = useCallback((index: number) => {
    setActiveQuestion(null);
    startTransition(() => setActiveTab(index));
  }, []);

  useEffect(() => {
    if (activeQuestion) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeQuestion]);

  const openModal = useCallback(
    (q: FaqQuestion) => {
      setActiveQuestion(q);
      setTimeout(() => {
        pushToDataLayer({
          event: "question_selected",
          page_type: "Home Page",
          cta_text: q.text,
          learner_type: currentPersona?.tab,
          page_url_custom: window.location.href,
          lead_starting_url: window.location.href,
          section_name: SECTION_NAME,
        });
      }, 2000);
    },
    [currentPersona]
  );

  const closeModal = useCallback(() => setActiveQuestion(null), []);

  const handleRelated = useCallback(
    (id: string) => {
      const found = currentPersona?.allQuestions.find((q) => q.id === id);
      if (!found) return;
      setActiveQuestion(found);
      pushToDataLayer({
        event: "question_selected",
        page_type: "Home Page",
        cta_text: found.text,
        learner_type: currentPersona?.tab,
        page_url_custom: window.location.href,
        lead_starting_url: window.location.href,
        section_name: SECTION_NAME,
      });
    },
    [currentPersona]
  );

  const handleCounsellor = useCallback(() => {
    closeModal();
    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.ConnectModalV2,
        modalData: {
          heading: "Take the 1st step towards your success story",
          submitBtnText: "Book a Free Session",
          isLandingPage: true,
          associatedPrograms,
        },
      },
    });

    setTimeout(() => {
      pushToDataLayer({
        event: "lead_initiate",
        cta_text: "Talk to a Counsellor",
        section_name: SECTION_NAME,
        page_type: getGlobalStateValue("pageType"),
        page_url: window?.location?.href || "NA",
      });

      updateStateSession({
        parentSection: SECTION_NAME,
      });
    }, 2000);
  }, [associatedPrograms, modalDispatch, closeModal]);

  const handlePrograms = useCallback(() => {
    pushToDataLayer({
      event: "navigation_click",
      category: "Programs",
      cta_text: "Talk to a Counsellor",
      section_name: SECTION_NAME,
      page_type: getGlobalStateValue("pageType"),
      page_url: window?.location?.href || "NA",
    });

    window.location.href = "/programs";
  }, []);

  return {
    heading,
    personas,
    rawPersonas: data,
    activeTab,
    activeQuestion,
    currentPersona,
    leftQuestions,
    rightQuestions,
    handleTabChange,
    openModal,
    closeModal,
    handleRelated,
    handleCounsellor,
    handlePrograms,
  };
}
